import type { SeoLocale } from "./types";

/**
 * Number and date formatting for the server-rendered SEO pages.
 *
 * Numbers group with a no-break space ("225 000"), the same trick the
 * trends pages use, so a price never wraps mid-number in a narrow
 * column. Month names are spelled out by hand rather than through
 * Intl: the Workers runtime's ICU data for "az" is not something to
 * bet a page title on, and the three sets are short.
 */

const NBSP = " ";

/**
 * Whole numbers group ("225 000"). A small fractional value keeps one
 * decimal: rent per m² is 11.4 AZN, and rounding it to 11 makes new-build
 * and resale rent look identical. The API sends one decimal only for
 * rent, so sale figures never grow a decimal here.
 */
export function num(n: number | null | undefined, locale: SeoLocale = "az"): string {
  if (n == null || !Number.isFinite(n)) return "";
  if (Math.abs(n) < 100 && !Number.isInteger(Math.round(n * 10) / 10)) {
    const s = (Math.round(n * 10) / 10).toFixed(1);
    return locale === "en" ? s : s.replace(".", ",");
  }
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

export function azn(n: number | null | undefined, locale: SeoLocale = "az"): string {
  const s = num(n, locale);
  return s ? `${s}${NBSP}AZN` : "";
}

export function ppmText(n: number | null | undefined, locale: SeoLocale): string {
  const s = num(n, locale);
  if (!s) return "";
  return `${s}${NBSP}${locale === "ru" ? "AZN/м²" : "AZN/m²"}`;
}

/** Signed percentage difference of a against b, rounded. */
export function pctDiff(a: number | null | undefined, b: number | null | undefined): number | null {
  if (a == null || b == null || !b) return null;
  return Math.round(((a - b) / b) * 100);
}

const MONTHS: Record<SeoLocale, string[]> = {
  az: ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"],
  ru: ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};

export function dateLong(iso: string | null | undefined, locale: SeoLocale): string {
  if (!iso) return "";
  const d = new Date(iso.endsWith("Z") || /[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return "";
  // Baku time (UTC+4, no DST): a listing scraped at 23:30 UTC belongs
  // to the next calendar day for everyone reading the page.
  const baku = new Date(d.getTime() + 4 * 3600 * 1000);
  const day = baku.getUTCDate();
  const month = MONTHS[locale][baku.getUTCMonth()];
  const year = baku.getUTCFullYear();
  return locale === "en" ? `${day} ${month} ${year}` : `${day} ${month} ${year}`;
}

export function yearOf(iso: string | null | undefined): number {
  const d = iso ? new Date(iso) : new Date();
  return Number.isNaN(d.getTime()) ? new Date().getUTCFullYear() : d.getUTCFullYear();
}

export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso.endsWith("Z") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86_400_000));
}

/** "{a}" placeholder substitution. */
export function fill(template: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce(
    (acc, [k, v]) => acc.split(`{${k}}`).join(String(v)),
    template,
  );
}

/** The longest of the candidates that fits a search result's title
 *  (about 65 characters), else the shortest one. Candidates go from the
 *  richest ("heading: 632 elan, ortalama 2 488 AZN/m² · Mənzil.ai") to the
 *  barest (the heading alone). */
export const TITLE_MAX = 65;
export function fitTitle(candidates: string[], max = TITLE_MAX): string {
  const list = candidates.filter(Boolean);
  return list.find((c) => c.length <= max) ?? list[list.length - 1];
}
